/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-type is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

// NewUser page components
import FormField from "modules/inspections/components/FormField";
import { useEffect, useState } from "react";
import { Box, Card, Switch, Icon } from "@mui/material";
import SoftSelect from "components/SoftComponents/SoftSelect";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftButton from "components/SoftComponents/SoftButton";
import Divider from '@material-ui/core/Divider';

import { point, distance } from "@turf/turf";
import DeviceSelectionCard from "components/Cards/DeviceSelectionCard";
import theme from "assets/theme";

import badgeDict from "modules/inspections/components/badgeDict.json";
import SprayCan from "assets/images/spray_can_white.png";
import BrokenDevice from "assets/images/broken_device_white.png";

import { actionItem } from "modules/inspections/utils/technician_actions";
import { filterOptions, calcDistanceSuffix } from "modules/inspections/utils/data";

const checkWorks = (values, actionsOther, setFieldValue, inspection_type) => {
    const hasStandardAction =
        values['devicePrimary-actions-removed'] ||
        values['devicePrimary-actions-cleaned'] ||
        values['devicePrimary-actions-battery_swap'] ||
        values['devicePrimary-actions-hardware_changed'] ||
        values['devicePrimary-actions-cosmetically_fixed'];

    const otherFieldHasText = values['devicePrimary-actions-other']?.trim().length > 0;
    const secondaryDeviceContent = values['deviceSecondary-uuid']?.trim().length > 0;

    if (values['devicePrimary-actions-removed']) {
        setFieldValue(inspection_type.name, secondaryDeviceContent ? "Unit Replacement" : "Unit Decommission");
    }
    else {
        setFieldValue(inspection_type.name, "Unit Rectification");
    }

    if (actionsOther) {
        return otherFieldHasText && !secondaryDeviceContent;
    }

    return hasStandardAction && !secondaryDeviceContent;
}

function TechnicianActions({ formData, deviceLayout }) {
    const {
        values, touched, errors, formField, isSubmitting,
        functions: { setDeviceOptions, setFieldValue, handleChange, handleBack, handleCancel },
        custom: { activeStep, isLastStep, user, all_devices, inspection_record, deviceOptions }
    } = formData;
    const { devicePrimary, deviceSecondary, inspection_type } = formField;

    const [works, setWorks] = useState(false);
    const [currentLocation, setCurrentLocation] = useState(values.location || null);
    const [removedState, setRemovedState] = useState(values?.['devicePrimary-actions-removed'] || false);
    const [actionsOther, setActionsOther] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setWorks(checkWorks(values, actionsOther, setFieldValue, inspection_type));
        if (!removedState) {
            handleClick(null);
        }
    }, [values, actionsOther]);

    useEffect(() => {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setCurrentLocation([latitude, longitude]);
                setIsLoading(false);
            },
            (error) => {
                console.error("Error detecting location:", error);
                // Fallback: perhaps set a default location or show manual entry
            },
            { enableHighAccuracy: true }
        );
    }, []);

    useEffect(() => {
        if (!isLoading) {
            const origin = point(currentLocation);
            const options = {
                units: "metres"
            };

            function locationSort(a, b) {
                const pointA = point([a.position[1], a.position[0]]);
                const pointB = point([b.position[1], b.position[0]]);

                const distanceA = distance(origin, pointA, options);
                const distanceB = distance(origin, pointB, options);

                return distanceA - distanceB;
            }

            const device_options = all_devices.sort(locationSort).map((device) => {
                return {
                    metadata: device,
                    value: {
                        name: device.name,
                        serial_number: device.serial_number,
                        stop_id: device.stop?.stop_id ? device.stop.stop_id : device.stop.site_id,
                        uuid: device.uuid,
                        distance_away: distance(origin, point([device.position[1], device.position[0]]), options)
                    },
                    label: device.name
                }
            });

            setDeviceOptions(device_options);
        }

    }, [isLoading]);

    const handleClick = (selected, e = false) => {
        if (e) e.stopPropagation();
        setFieldValue(deviceSecondary.info.name, selected ? selected : "");
        setFieldValue(deviceSecondary.uuid.name, selected ? selected.value.uuid : "");
        setFieldValue(deviceSecondary.stop_id.name, selected ? selected.value.stop_id : "");
        setFieldValue(deviceSecondary.serial_no.name, selected ? selected.value.serial_number : "");
        setFieldValue(deviceSecondary.actions.installed.name, selected ? true : false);
        setFieldValue(deviceSecondary.actions.cleaned.name, selected ? true : false);
    }

    return (
        <SoftBox>
            <SoftBox>
                <SoftBox mb={3} display="flex" flexDirection="column" alignItems="center">
                    <SoftTypography variant="h4" fontWeight="bold">
                        Maintenance / Repairs
                    </SoftTypography>
                </SoftBox>
                <SoftBox mt={3}>
                    <Divider />
                    <SoftBox mt={2}>
                        <SoftBox display="flex" flex={1} flexDirection='column'>
                            <SoftBox pb={1}>
                                <SoftTypography variant={deviceLayout == "mobile" ? "mobileBody1" : "subtitle2"} fontWeight="bold">
                                    Inspected Device:
                                </SoftTypography>
                                <Grid container mt={0.5} mb={3}>
                                    <Grid item xxs={12} sm={4}>
                                        <SoftBox display="flex" flexDirection="column" pt={2} pb={2} px={{ xxs: 0, sm: 1 }}>
                                            <SoftBox mb={1} ml={0.5}>
                                                <SoftTypography variant="caption" fontWeight="bold">
                                                    Device Details
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftBox display="flex" flexDirection="column" pl={0.5}>
                                                <SoftTypography variant={deviceLayout == 'mobile' ? "body5" : "body3"} color="text">
                                                    {inspection_record.organisation_id == 'clearways' ? `eMinor Label: ${values['devicePrimary-info'].label}` : `Name: ${values['devicePrimary-info'].label}`}
                                                </SoftTypography>
                                                <SoftTypography variant={deviceLayout == 'mobile' ? "body5" : "body3"} color="text">
                                                    {inspection_record.organisation_id == 'clearways' ? `Site ID: ${values['devicePrimary-stop_id']}` : `Stop ID: ${values['devicePrimary-stop_id']}`}
                                                </SoftTypography>
                                                <SoftTypography variant={deviceLayout == 'mobile' ? "body5" : "body3"} color="text">
                                                    Serial No: {values['devicePrimary-serial_no']}
                                                </SoftTypography>
                                            </SoftBox>
                                        </SoftBox>
                                        <SoftBox sx={{
                                            opacity: 1,
                                            [theme.breakpoints.up('sm')]: {
                                                opacity: 0
                                            }
                                        }}>
                                            <Divider orientation="horizontal" />
                                        </SoftBox>
                                    </Grid>
                                    <Divider orientation="vertical" flexItem />

                                    {(inspection_record.devices[0].conditions && Object.entries(inspection_record.devices[0].conditions).filter((row) => row[1] === true).length > 0) && (
                                        <Grid item xxs={12} sm={4}>
                                            <SoftBox display="flex" flexDirection="column" py={2} px={{ xxs: 0, sm: 2 }}>
                                                <SoftBox mb={1} ml={0.5}>
                                                    <SoftTypography variant="caption" fontWeight="bold">
                                                        Device Conditions
                                                    </SoftTypography>
                                                </SoftBox>
                                                {Object.entries(inspection_record.devices[0].conditions).filter((row) => row[1] === true || (row[0] == "other" && row[1] != "")).map(([key, value], conIndex) => (
                                                    <SoftBox key={conIndex}>
                                                        <SoftBox pl={0.5} pb={0.25} display="flex" flexDirection="row" alignItems="center">
                                                            {badgeDict[key].icon == "SprayCan" && (
                                                                <SoftBox component="img" src={SprayCan} variant="gradient" bgColor={badgeDict[key].color} sx={{ borderRadius: '4px', width: '24px', height: '24px' }} />
                                                            )}
                                                            {badgeDict[key].icon == 'BrokenDevice' && (
                                                                <SoftBox component="img" src={BrokenDevice} variant="gradient" bgColor={badgeDict[key].color} sx={{ borderRadius: '4px', width: '24px', height: '24px' }} />
                                                            )}
                                                            {(badgeDict[key].icon != "BrokenDevice" && badgeDict[key].icon != "SprayCan") && (
                                                                <SoftBox variant="gradient" bgColor={badgeDict[key].color} sx={{ borderRadius: '4px', width: '24px', height: '24px' }}>
                                                                    <Icon fontSize="22px !important" sx={{ color: "#fff", fontSize: '22px', display: "flex", alignItems: "center", justifyContent: "center", width: '24px !important', height: '24px !important' }}>{badgeDict[key].icon}</Icon>
                                                                </SoftBox>
                                                            )}
                                                            <SoftTypography pl={1} variant="subtitle2" textTransform="capitalize">{key.replace('_', ' ')}</SoftTypography>
                                                        </SoftBox>
                                                        {key == 'other' && (
                                                            <SoftBox pl={5}>
                                                                <SoftTypography variant="caption">{value}</SoftTypography>
                                                            </SoftBox>
                                                        )}
                                                    </SoftBox>
                                                ))}
                                            </SoftBox>
                                            <SoftBox sx={{
                                                opacity: 1,
                                                [theme.breakpoints.up('sm')]: {
                                                    opacity: 0
                                                }
                                            }}>
                                                <Divider orientation="horizontal" />
                                            </SoftBox>
                                        </Grid>
                                    )}
                                    <Divider orientation="vertical" flexItem />

                                    {(inspection_record.devices[0].actions && Object.entries(inspection_record.devices[0].actions).filter((row) => row[1] === true).length > 0) && (
                                        <Grid item xxs={12} sm={4}>
                                            <SoftBox display="flex" flexDirection="column" py={2} px={{ xxs: 0, sm: 2 }}>
                                                <SoftBox mb={1} ml={0.5}>
                                                    <SoftTypography variant="caption" fontWeight="bold">
                                                        Technician Actions
                                                    </SoftTypography>
                                                </SoftBox>
                                                {Object.entries(inspection_record.devices[0].actions).filter((row) => row[1] === true).map((arr, actIndex) => (
                                                    <SoftBox pl={0.5} pb={0.25} key={actIndex} display="flex" flexDirection="row" alignItems="center">
                                                        <SoftBox variant="gradient" bgColor={'success'} sx={{ borderRadius: "4px", width: "24px", height: "24px" }}>
                                                            <Icon fontSize="22px !important" sx={{ color: "#fff", fontSize: "22px", display: "flex", alignItems: "center", justifyContent: "center", width: "24px !important", height: "24px !important" }}>check</Icon>
                                                        </SoftBox>
                                                        <SoftTypography pl={1} variant="subtitle2" textTransform="capitalize">{arr[0].replace('_', ' ')}</SoftTypography>
                                                    </SoftBox>
                                                ))}
                                            </SoftBox>
                                            <SoftBox sx={{
                                                opacity: 1,
                                                [theme.breakpoints.up('sm')]: {
                                                    opacity: 0
                                                }
                                            }}>
                                                <Divider orientation="horizontal" />
                                            </SoftBox>
                                        </Grid>
                                    )}
                                </Grid>
                            </SoftBox>
                            <SoftBox pb={1}>
                                <SoftTypography variant={deviceLayout == "mobile" ? "mobileBody1" : "subtitle2"} fontWeight={deviceLayout == "mobile" ? "bold" : "regular"}>
                                    Please select the rectifications you have made to the <b>original device</b>, or select 'Other' to add another.
                                </SoftTypography>
                            </SoftBox>
                            <Grid container spacing={1}>
                                {actionItem('assignment', 'removed', 'Removed/Replaced', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                {actionItem('assignment', 'cleaned', 'Cleaned', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                {actionItem('assignment', 'battery_swap', 'Battery Swap', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                {actionItem('assignment', 'hardware_changed', 'Hardware Changed', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                {actionItem('assignment', 'cosmetically_fixed', 'Cosmetically Fixed', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                <Grid item xxs={6}>
                                    <SoftBox display="flex" justifyContent="center" py={3} px={1}
                                        onClick={(e) => {
                                            setActionsOther(!actionsOther);
                                        }}
                                        sx={({ palette: { gradients }, functions: { rgba, linearGradient } }) => ({
                                            borderRadius: '10px',
                                            cursor: 'pointer',
                                            background: linearGradient(rgba(gradients[(actionsOther ? 'secondary' : 'light')].main, 0.6), rgba(gradients[(actionsOther ? 'secondary' : 'light')].state, 0.6)),
                                            border: '1px solid #aaa'
                                        })}>
                                        <SoftBox display="flex" alignItems="center" sx={{ paddingTop: '2.5px' }}>
                                            <SoftTypography variant={deviceLayout == "mobile" ? "mobileBody1" : "body2"} fontWeight={'bold'}>Other</SoftTypography>
                                        </SoftBox>
                                    </SoftBox>
                                </Grid>
                                {actionsOther && (
                                    <Grid item xxs={12}>
                                        <SoftBox>
                                            <FormField
                                                type={devicePrimary.actions.other.type}
                                                label={devicePrimary.actions.other.label}
                                                name={devicePrimary.actions.other.name}
                                                value={values['devicePrimary-actions-other']}
                                                placeholder={devicePrimary.actions.other.placeholder}
                                                onChange={handleChange}
                                            />
                                        </SoftBox>
                                    </Grid>
                                )}
                            </Grid>
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
            {removedState && (
                <SoftBox mt={4}>
                    <Grid container spacing={3}>
                        <Grid item xxs={12} lg={6}>
                            <SoftBox display="flex" flexDirection="column" alignItems={{ xxs: "center", lg: 'flex-start' }}>
                                <SoftTypography variant="h4" fontWeight="bold" textAlign={{ xxs: "center", lg: "left" }}>
                                    Replacement Search
                                </SoftTypography>
                            </SoftBox>
                            <SoftBox mb={1} display="flex" flexDirection="column" alignItems={{ xxs: "center", lg: 'flex-start' }}>
                                <SoftTypography pt={1} variant="mobileBody1" textAlign={{ xxs: "center", lg: "left" }}>
                                    If there is a <b>replacement</b> device, type to search by:<br /><b>Device Name/Stop Name</b>, <b>Stop ID</b>, <b>Serial Number</b>, or <b>Site ID</b>
                                </SoftTypography>
                            </SoftBox>
                            <SoftBox mb={2} sx={({ palette: { white }, borders: { borderRadius } }) => ({ maxWidth: '100%', borderRadius: borderRadius.md, backgroundColor: white.main })}>
                                <SoftSelect
                                    name={deviceSecondary.info.name}
                                    value={deviceOptions.find(opt => opt.label === values['deviceSecondary-info']?.label) || ""}
                                    onChange={(selected) => handleClick(selected)}
                                    filterOption={filterOptions}
                                    options={deviceOptions}
                                    isClearable={true}
                                    isSearchable={true}
                                    placeholder="Search or select a device..."

                                    error={errors.deviceSecondary?.info && touched.deviceSecondary?.info}
                                />
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={12} lg={6}>
                            {typeof values['deviceSecondary-info'] != 'string' && (
                                <DeviceSelectionCard
                                    title={values['deviceSecondary-info']?.label}
                                    description={[`Stop ID: ${values['deviceSecondary-stop_id']}`, `Serial No.: ${values['deviceSecondary-serial_no']}`]}
                                    action={{ func: isSubmitting, type: "submit", label: "select" }}
                                    value={calcDistanceSuffix(values['deviceSecondary-info']?.value.distance_away)}
                                />
                            )}
                        </Grid>
                    </Grid>
                </SoftBox>
            )}
            <SoftBox mt={5} width="100%" display="flex" justifyContent="space-between">
                <SoftButton variant="gradient" color={activeStep === 0 ? "error" : "light"} onClick={activeStep === 0 ? handleCancel : handleBack}>
                    {activeStep === 0 ? "cancel" : "back"}
                </SoftButton>
                {works && (
                    <SoftButton
                        disabled={isSubmitting}
                        type="submit"
                        variant="gradient"
                        color="dark"
                    >
                        {isLastStep ? "submit" : "next"}
                    </SoftButton>
                )}
            </SoftBox>
        </SoftBox >
    );
}

// typechecking props for TechnicianActions
TechnicianActions.propTypes = {
    formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
    deviceLayout: PropTypes.string,
};

export default TechnicianActions;
